/*
 * Breadcrumb derivation — given a pathname, walks the typed nav trees
 * to produce a trail like:
 *   Admin → Sync & Streaming → Transfer Logs → Run Detail
 * for the URL /admin/sync/transfer-logs/run-123.
 *
 * Approach: progressively shorten the path looking for nav-item matches
 * (so /admin/sync/transfer-logs/run-123 finds the Transfer Logs item
 * and treats `run-123` as a detail-page leaf).
 */

import type { NavItem, NavTree } from "./navigationTypes.js";

export interface Breadcrumb {
  label: string;
  to?: string;            // omitted for the leaf crumb (current page)
}

export interface BreadcrumbContext {
  consoleLabel: string;   // "Admin" | "Workspace" | "DHIS2 App"
  trees: NavTree[];
  /**
   * Optional resolver: given a "leaf" path segment that doesn't map to a
   * nav item (e.g. /admin/workspaces/:id), produce a label for it.
   * Defaults to the segment itself.
   */
  resolveLeafLabel?: (path: string, segment: string) => string;
}

function findItemByExactPath(pathname: string, trees: NavTree[]): { item: NavItem; tree: NavTree; groupLabel: string } | null {
  for (const tree of trees) {
    for (const group of tree.groups) {
      for (const item of group.items) {
        if (item.to === pathname) return { item, tree, groupLabel: group.label };
      }
    }
  }
  return null;
}

function findClosestParentItem(pathname: string, trees: NavTree[]): { item: NavItem; tree: NavTree; groupLabel: string; remainder: string } | null {
  // Walk up parent paths; first nav-item match wins.
  let current = pathname;
  while (current.length > 0 && current !== "/") {
    const slash = current.lastIndexOf("/");
    if (slash <= 0) break;
    const parent = current.slice(0, slash);
    const match = findItemByExactPath(parent, trees);
    if (match) {
      return { ...match, remainder: pathname.slice(parent.length).replace(/^\//, "") };
    }
    current = parent;
  }
  return null;
}

export function deriveBreadcrumbs(pathname: string, ctx: BreadcrumbContext): Breadcrumb[] {
  const root: Breadcrumb = { label: ctx.consoleLabel, to: ctx.trees[0]?.basePath };

  // Exact match first — current page IS a nav item.
  const exact = findItemByExactPath(pathname, ctx.trees);
  if (exact) {
    return [
      root,
      { label: exact.groupLabel },
      { label: exact.item.label }
    ];
  }

  // Detail-page case: parent path matches a nav item, remainder is the
  // detail leaf (e.g. transfer-logs/run-123).
  const parentMatch = findClosestParentItem(pathname, ctx.trees);
  if (parentMatch) {
    const leafLabel = ctx.resolveLeafLabel
      ? ctx.resolveLeafLabel(pathname, parentMatch.remainder)
      : parentMatch.remainder;
    return [
      root,
      { label: parentMatch.groupLabel },
      { label: parentMatch.item.label, to: parentMatch.item.to },
      { label: leafLabel }
    ];
  }

  // Fall back to console root + raw path segments.
  const segments = pathname.split("/").filter(Boolean);
  return [root, ...segments.map((s) => ({ label: s }))];
}
